import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';

function buildEmptyForm() {
  return {
    profileUid: '',
    notes: '',
  };
}

export function useComplianceSar() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeUid, setActiveUid] = useState('');
  const [form, setForm] = useState(buildEmptyForm());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');

    try {
      const response = await adminApi.getSarQueue(token);
      setItems(Array.isArray(response?.items) ? response.items : []);
    } catch (requestError) {
      setItems([]);
      setError(requestError.message || 'Failed to load SAR queue');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const submitCreate = useCallback(async (event) => {
    event.preventDefault();

    if (!String(form.profileUid || '').trim()) {
      setError('Profile UID is required to create a SAR request.');
      setSuccess('');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await adminApi.createSar(token, form.profileUid, {
        notes: String(form.notes || '').trim() || null,
      });
      setForm(buildEmptyForm());
      setSuccess('SAR request created.');
      await load();
    } catch (requestError) {
      setError(requestError.message || 'Failed to create SAR request');
    } finally {
      setIsSubmitting(false);
    }
  }, [form.notes, form.profileUid, load, token]);

  const complete = useCallback(async (uid) => {
    const normalizedUid = String(uid || '').trim();
    if (!normalizedUid) {
      return;
    }

    const notes = window.prompt('Completion notes (optional):', '') ?? '';

    setActiveUid(normalizedUid);
    setError('');
    setSuccess('');

    try {
      await adminApi.completeSar(token, normalizedUid, {
        notes: String(notes || '').trim() || null,
      });
      setSuccess('SAR request completed.');
      await load();
    } catch (requestError) {
      setError(requestError.message || 'Failed to complete SAR request');
    } finally {
      setActiveUid('');
    }
  }, [load, token]);

  return {
    items,
    isLoading,
    isSubmitting,
    activeUid,
    form,
    error,
    success,
    onFormChange,
    submitCreate,
    complete,
    reload: load,
  };
}
